import { FaixaIndicadores } from "./pecasPremium";
import { useEffect, useState } from "react";
import { Layers, Receipt, TrendingUp, Users } from "lucide-react";

import { formatBRLInteger } from "@/lib/format";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { todasAsLinhas } from "@/components/atomic-crm/providers/supabase/todasAsLinhas";
import { useRoleDataScope } from "../../commons/useRoleDataScope";
import { useMascara } from "../../valores-ocultos/contexto";

/**
 * Resumo no topo da página de Vendas — sempre visível, e ACOMPANHANDO o filtro.
 *
 * Primeiro eu fiz o contrário: fixo no ano, ignorando os filtros, pra servir de
 * régua estável. Estava errado — um total de 10 milhões parado em cima de uma
 * lista filtrada que mostra "nenhuma venda registrada" não é régua, é
 * contradição na mesma tela. "Sempre aparecer" era sobre não sumir, não sobre
 * ignorar o recorte.
 *
 * Segue PERÍODO e VENDEDOR, que são os filtros que mudam o universo. Não segue
 * busca por texto nem aba: busca é para achar uma linha, e as abas são estados
 * diferentes da venda — o resumo é sempre de venda CONFIRMADA, senão "total de
 * vendas" mudaria de significado ao trocar de aba.
 *
 * Sem período escolhido, o padrão é o ano corrente, e isso está escrito na
 * tela: "total de vendas" sem período cada pessoa interpreta de um jeito.
 *
 * ESCOPO vem da RLS: servopa_vendas_with_details é security_invoker, então
 * vendedor vê o dele e gerente vê o time. Não filtramos por usuário aqui —
 * seria reimplementar autorização no cliente, e divergir dela algum dia.
 *
 * Só venda CONFIRMADA entra. Pendente ainda não é venda e cancelada deixou de
 * ser; misturar as três daria um total que não bate com nenhuma aba.
 */

/** 2026-08-01 -> 01/08/2026. */
const formatarData = (iso: string) => iso.split("-").reverse().join("/");

type Resumo = {
  total: number;
  cotas: number;
  especialistas: number;
};

export const ResumoVendas = ({
  de,
  ate,
  salesPersonIds = [],
}: {
  /** Início do período. Vazio = 1º de janeiro do ano corrente. */
  de?: string;
  /** Fim do período. Vazio = 31 de dezembro do ano corrente. */
  ate?: string;
  /** Vazio = todos os vendedores no escopo de quem está olhando; vários = a soma deles. */
  salesPersonIds?: number[];
}) => {
  const { isLoading } = useRoleDataScope();
  const m = useMascara();
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const ano = new Date().getFullYear();
  const inicio = de || `${ano}-01-01`;
  const fim = ate || `${ano}-12-31`;
  const chaveVendedores = salesPersonIds.join(",");

  useEffect(() => {
    if (isLoading) return;
    let ativo = true;
    (async () => {
      // Em páginas: o ano inteiro de vendas passa das 1000 linhas do PostgREST.
      const montar = () => {
        let q = supabase
          .from("servopa_vendas_with_details")
          .select("valor_credito, sales_person_id")
          .eq("status", "confirmed")
          .gte("venda_data", inicio)
          .lte("venda_data", fim);
        if (chaveVendedores) q = q.in("sales_person_id", chaveVendedores.split(",").map(Number));
        return q;
      };
      const { data, error } = await todasAsLinhas<{ valor_credito: number | null; sales_person_id: number | null }>(montar);
      if (!ativo || error) return;

      const linhas = data ?? [];
      setResumo({
        total: linhas.reduce((s, v) => s + Number(v.valor_credito || 0), 0),
        cotas: linhas.length,
        // DISTINCT no cliente: são poucos vendedores e a alternativa seria uma
        // view só pra isso. Quem não vendeu não aparece — "produziram" é quem
        // tem venda, não quem está cadastrado.
        especialistas: new Set(
          linhas.map((v) => v.sales_person_id).filter((id) => id != null),
        ).size,
      });
    })();
    return () => {
      ativo = false;
    };
  }, [isLoading, inicio, fim, chaveVendedores]);

  // O rótulo diz QUAL recorte está sendo somado. Sem isso, o número muda ao
  // mexer no filtro e não dá pra saber se mudou porque o filtro mudou ou
  // porque o dado mudou.
  const periodo =
    de || ate ? `${formatarData(inicio)} a ${formatarData(fim)}` : `no ano de ${ano}`;

  return (
    <FaixaIndicadores
      itens={[
        {
          icone: <TrendingUp />,
          rotulo: "Total de vendas",
          valor: resumo ? m(formatBRLInteger(resumo.total)) : "…",
          detalhe: `confirmadas ${periodo}`,
        },
        {
          icone: <Layers />,
          rotulo: "Cotas vendidas",
          valor: resumo ? m(resumo.cotas) : "…",
          detalhe: periodo,
        },
        {
          icone: <Users />,
          rotulo: "Especialistas que produziram",
          valor: resumo ? m(resumo.especialistas) : "…",
          detalhe: chaveVendedores ? "no filtro atual" : "com pelo menos uma venda",
        },
        {
          // Total do grupo ÷ cotas do grupo — nunca a média das médias (cartão do Pedro).
          icone: <Receipt />,
          rotulo: "Ticket médio",
          valor: resumo ? (resumo.cotas ? m(formatBRLInteger(resumo.total / resumo.cotas)) : "—") : "…",
          detalhe: periodo,
        },
      ]}
    />
  );
};
